import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getTenantCallsApi } from '../../apis/apis'
import { Paginated, Note, TenantCallsSearchParams } from '../../apis/types'
import type { RootState, AppDispatch } from '../../shared/redux/store'

const SLICE_NAME = 'CALLS_SLICE'

function callsQueryKey(params: { tenantCuid: string } & TenantCallsSearchParams): string {
  return [params.tenantCuid, params.page ?? 0, params.resultsPerPage ?? 0].join('|')
}

export const getCalls = createAsyncThunk<
  Paginated<Note>,
  { tenantCuid: string } & TenantCallsSearchParams,
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(`${SLICE_NAME}/getCalls`, async ({ tenantCuid, ...query }, { rejectWithValue }) => {
  try {
    const response = await getTenantCallsApi(tenantCuid, query)
    return { ...response }
  } catch (err) {
    return rejectWithValue({
      err: err !== undefined,
    })
  }
})

interface CallsState {
  loading: boolean
  error: boolean
  callRequestPhase: { kind: 'idle' } | { kind: 'in-progress' } | { kind: 'failed' }
  callsQueryKey: string | null
  calls: Note[]
  total: number
  resultsPerPage: number
  page: number
}

const initialState: CallsState = {
  loading: false,
  error: false,
  callRequestPhase: { kind: 'idle' },
  callsQueryKey: null,
  calls: [],
  total: 0,
  resultsPerPage: 0,
  page: 0,
}

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    clearCalls: (state) => {
      state.calls = []
      state.total = 0
      state.page = 0
      state.callsQueryKey = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCalls.pending, (state, action) => {
      const newKey = callsQueryKey(action.meta.arg)
      const prevTenant = state.callsQueryKey?.split('|')[0]
      const newTenant = action.meta.arg.tenantCuid
      if (prevTenant !== newTenant) {
        state.calls = []
        state.total = 0
      }
      state.callRequestPhase = { kind: 'in-progress' }
      state.callsQueryKey = newKey
    })
    builder.addCase(getCalls.fulfilled, (state, action) => {
      if (callsQueryKey(action.meta.arg) !== state.callsQueryKey) return
      const { payload } = action
      state.callRequestPhase = { kind: 'idle' }
      state.calls = payload.results || []
      state.total = payload.total ?? 0
      state.resultsPerPage = payload.resultsPerPage ?? 0
      state.page = payload.page ?? 0
    })
    builder.addCase(getCalls.rejected, (state, { payload, error, meta }) => {
      if (callsQueryKey(meta.arg) !== state.callsQueryKey) return
      if (payload != null) {
        state.callRequestPhase = { kind: 'failed' }
      } else {
        state.error = error !== undefined
      }
    })
  },
})

export const { clearCalls } = slice.actions
export const callsReducer = slice.reducer
